import type { DamageType } from '../../../shared/game/combat-types.js';

/**
 * How each kind of damage kicks a living body (see `hit-springs.ts`). `push` scales the kick that tips the trunk away from the blow, `twist`
 * the turn about the spine when struck off-center, `head` the extra snap of a head, `fling` how freely arms, tails and ears lag behind the
 * body, `tremble` a shiver laid over the recovery, and `settle` the seconds a blow takes to die away.
 */
export const HIT_PROFILES: Record<DamageType, { push: number; twist: number; head: number; fling: number; tremble: number; settle: number }> = {
  slashing: { push: 1, twist: 1.3, head: 1, fling: 1, tremble: 0, settle: .45 },
  piercing: { push: .9, twist: .3, head: .8, fling: .6, tremble: 0, settle: .5 },
  bludgeoning: { push: 1.35, twist: .8, head: 1.2, fling: 1.3, tremble: 0, settle: .6 },
  fire: { push: .9, twist: .5, head: 1, fling: 1.5, tremble: .5, settle: .55 },
  cold: { push: .7, twist: .2, head: .6, fling: .5, tremble: 1, settle: .7 },
  lightning: { push: 1.1, twist: .4, head: 1.3, fling: 1.6, tremble: 1.6, settle: .4 },
  psychic: { push: .35, twist: .2, head: 2.4, fling: .4, tremble: .6, settle: .6 },
  radiant: { push: .85, twist: .6, head: 1.2, fling: 1.2, tremble: .3, settle: .5 },
  necrotic: { push: .8, twist: .3, head: .9, fling: .6, tremble: .3, settle: .65 },
};

/** Spring tuning shared by every family, in radians and seconds. */
export const hitFlinch = {
  /** Damping ratio: one overshoot past the pose, then still. */
  damping: .55,
  /** Peak trunk tilt of a graze and how much more the whole of a body's health adds (by its square root). */
  tiltGraze: .08, tiltFull: .3,
  /** How freely each part's own orientation lags its parent's motion (0 follows rigidly, 1 hangs free). */
  lag: { trunk: .25, head: .55, limb: .8 },
  /** Settling stretches with a blow's strength: a graze takes this share of the profile's time, a full-health blow `.85 + .4`. */
  settleGraze: .85, settleStrength: .4,
  /** Share of a blow the trunk of a body standing on it takes (a beast's back over four legs); the rest goes to its head. */
  braced: .35,
  /** Largest tilt any part may reach, so a pile of blows stays a stagger. */
  maxTilt: .7,
  /** Below this motion (radians, radians per second) the springs rest and stop costing anything. */
  rest: .002,
  /** A blow seen later than this after it landed (joining, rewinding) is not replayed. */
  stale: .4,
} as const;
